import { del, get, patch, post } from "@/infra/rest";
import requestConfig from "@/shared/config/request";

export const addToSlack = async () => {
  const urlObj = new URL(requestConfig.slackUrl);
  return get<{
    install_url: string;
  }>({
    url: urlObj.pathname + urlObj.search,
    isAuthRequired: false,
    config: {
      baseURL: urlObj.origin,
    },
  });
};

export interface ControlSet {
  controls_set_name: string;
  controls_url: string;
  default_control_set: boolean;
  id: string;
  controls_set_published: boolean;
  created_by: string;
  created_on: string;
  updated_by: string;
  updated_on: string;
  number_of_controls: number;
  last_updated_by: string;
  last_updated_on: string;
}

export interface CustomControlSetResponse {
  success: boolean;
  control_sets: {
    multi_file_vendor_assessment: ControlSet[];
  };
}
export interface QuestionnaireDetailsResponse {
  success: boolean;
  questionnaire: {
    id: string;
    organization_id: string;
    questionnaire_name: string;
    number_of_questions: number;
    questionnaire_json_url: string | null;
    questions_document_url: string | null;
    type: "multi_file_vendor_assessment";
    created_on: string;
    created_by: string;
    last_updated_by: string;
    last_updated_on: string;
    published: boolean;
  };
}

export const getCustomControlSets = async () => {
  return get<CustomControlSetResponse>({
    url: '/v0/custom-control-set',
    isAuthRequired: true,
  });
};

export interface UpdateControlSetPayload {
  controls_url: string;
  operation_type: "update_control_set_controls";
}

export interface UpdateControlSetResponse {
  success: boolean;
  message: string;
  control_set: ControlSet;
}

export const updateControlSetControls = async (
  controlSetId: string,
  payload: UpdateControlSetPayload
) => {
  return patch<UpdateControlSetResponse>({
    url: `/v0/custom-control-set/${controlSetId}`,
    config: {
      data: payload,
    },
    isAuthRequired: true,
  });
};

export interface DeleteControlSetResponse {
  success: boolean;
}

export const deleteControlSet = async (controlSetId: string) => {
  return del<DeleteControlSetResponse>({
    url: `/v0/custom-control-set/${controlSetId}`,
    isAuthRequired: true,
  });
};

export interface MarkControlSetDefaultPayload {
  default_control_set: boolean;
  operation_type: "mark_control_set_default";
}

export interface MarkControlSetDefaultResponse {
  success: boolean;
  control_set_state: {
    controls_set_name: string;
    controls_url: string;
    default_control_set: boolean;
    id: string;
    control_set_published: boolean;
    created_by: string;
    created_on: string;
    updated_by: string;
    updated_on: string;
  };
}

export const markControlSetAsDefault = async (controlSetId: string) => {
  return patch<MarkControlSetDefaultResponse>({
    url: `/v0/custom-control-set/${controlSetId}`,
    config: {
      data: {
        default_control_set: true,
        operation_type: "mark_control_set_default",
      },
    },
    isAuthRequired: true,
  });
};

export interface RenameControlSetPayload {
  new_controls_set_name: string;
  current_controls_set_name: string;
  operation_type: "rename_control_set";
}

export interface RenameControlSetResponse {
  success: boolean;
  control_set_state: {
    controls_set_name: string;
    controls_url: string;
    default_control_set: boolean;
    id: string;
    control_set_published: boolean;
    created_by: string;
    created_on: string;
    updated_by: string;
    updated_on: string;
  };
}

export const renameControlSet = async (
  controlSetId: string,
  newName: string,
  currentName: string
) => {
  return patch<RenameControlSetResponse>({
    url: `/v0/custom-control-set/${controlSetId}`,
    config: {
      data: {
        new_controls_set_name: newName,
        current_controls_set_name: currentName,
        operation_type: "rename_control_set",
      },
    },
    isAuthRequired: true,
  });
};

export interface CreateControlSetPayload {
  controls_set_name: string;
}

export interface CreateControlSetResponse {
  success: boolean;
  control_set_state: {
    organization_id: string;
    id: string;
    controls_set_name: string;
    controls_url: string;
    number_of_controls: number;
    type: "multi_file_vendor_assessment";
    default_control_set: boolean;
    control_set_published: boolean;
    zania_default_control_set: boolean;
    created_by: string;
    created_on: string;
    last_updated_by: string;
    last_updated_on: string;
    deleted: boolean;
  };
}

export const createControlSet = async (controlSetName: string) => {
  return post<CreateControlSetPayload, CreateControlSetResponse>({
    url: '/v0/custom-control-set/create',
    data: {
      controls_set_name: controlSetName,
    },
    isAuthRequired: true,
  });
};


export interface CreateQuestionnairePayload {
  questionnaire_name: string;
  type: string;
}

export interface AddQuestionToQuestionnairePayload {
  question: AddNewQuestionPayload;
  type: string;
}

export interface CreateQuestionnaireResponse {
  success: boolean;
  questionnaire: {
    "id": string,
    "organization_id": string,
    "questionnaire_name": string,
    "questions": any[],
    "number_of_questions": number,
    "questionnaire_json_url": string | null,
    "questions_document_url": string | null,
    "type": "multi_file_vendor_assessment",
    "created_on": string,
    "created_by": string,
    "last_updated_by": string,
    "last_updated_on": string,
    "published": boolean
  };
}

export const createNewQuestionnaire = async (questionnaireName: string) => {
  return post<CreateQuestionnairePayload, CreateQuestionnaireResponse>({
    url: '/v0/tpra/questionnaires/create',
    data: {
      questionnaire_name: questionnaireName,
      type: "multi_file_vendor_assessment"
    },
    isAuthRequired: true,
  });
};

export type QuestionType = "free_text" | "single_choice" | "multiple_choice";

export interface AddNewQuestionPayload {
  question: string;
  question_type: QuestionType;
  is_dropdown: boolean;
  response_choices: string[];
  required: boolean;
  comments_allowed: boolean;
  parent_question_id?: string;
  parent_question_response_option?: string;
}


export const addNewQuestionToQuestionnaire = async (questionnaire_id: string, questionData: AddNewQuestionPayload) => {
  return post<AddQuestionToQuestionnairePayload, CreateQuestionnaireResponse>({
    url: `/v0/tpra/questionnaires/${questionnaire_id}/question/add`,
    data: {
      question: questionData,
      type: "multi_file_vendor_assessment"
    },
    isAuthRequired: true,
  });
};

export const getQuestionnaireDetails = async (id: string) => {
  return get<QuestionnaireDetailsResponse>({
    url: `/v0/tpra/questionnaires/id/${id}`,
    isAuthRequired: true,
  });
};